import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { useLocation } from "react-router";
import { z } from "zod";
import { useAccountState } from "@made-people/grease-account";
import { useNewsletter } from "@made-people/grease-newsletter";
import { hasStatisticsConsent } from "../../hooks/use-cookiebot-consent";
import {
  clearStoredMmVisitorEmail,
  getStoredMmVisitorEmail,
  setStoredMmVisitorEmail,
} from "./mm-visitor-email-storage";
import type { NewsletterEvent } from "@made-people/grease-newsletter";

const visitorEmailSchema = z.string().trim().email();

const parseVisitorEmail = (value: unknown): string | undefined => {
  const parsed = visitorEmailSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
};

type MmVisitorEmailContextValue = {
  email: string | undefined;
  setVisitorEmail: (value: unknown) => string | undefined;
};

const MmVisitorEmailContext = createContext<
  MmVisitorEmailContextValue | undefined
>(undefined);

export const MmVisitorEmailProvider = (props: PropsWithChildren) => {
  const { customer } = useAccountState();
  const { newsletterEventEmitter } = useNewsletter();
  const location = useLocation();
  const [visitorEmail, setVisitorEmail] = useState<string | undefined>();

  const trySetVisitorEmail = useCallback((value: unknown) => {
    if (!hasStatisticsConsent()) {
      return;
    }
    const email = parseVisitorEmail(value);
    if (!email) {
      return;
    }
    setVisitorEmail(email);
    setStoredMmVisitorEmail(email);
    return email;
  }, []);

  useEffect(() => {
    const restoreVisitorEmail = () => {
      if (!hasStatisticsConsent()) {
        return;
      }

      const customerEmail = parseVisitorEmail(customer?.email);
      const queryEmail = parseVisitorEmail(
        new URLSearchParams(location.search).get("customer_email"),
      );
      trySetVisitorEmail(
        customerEmail ?? queryEmail ?? getStoredMmVisitorEmail(),
      );
    };
    const clearVisitorEmail = () => {
      setVisitorEmail(undefined);
      clearStoredMmVisitorEmail();
    };
    const syncConsent = () => {
      if (hasStatisticsConsent()) {
        restoreVisitorEmail();
      } else {
        clearVisitorEmail();
      }
    };

    restoreVisitorEmail();
    window.addEventListener("CookiebotOnConsentReady", syncConsent);
    window.addEventListener("CookiebotOnAccept", syncConsent);
    window.addEventListener("CookiebotOnDecline", clearVisitorEmail);
    return () => {
      window.removeEventListener("CookiebotOnConsentReady", syncConsent);
      window.removeEventListener("CookiebotOnAccept", syncConsent);
      window.removeEventListener("CookiebotOnDecline", clearVisitorEmail);
    };
  }, [customer?.email, location.search, trySetVisitorEmail]);

  useEffect(() => {
    const onSubscribe = (event: NewsletterEvent["subscribe"]) => {
      trySetVisitorEmail(event.payload.email);
    };
    newsletterEventEmitter.on("subscribe", onSubscribe);
    return () => {
      newsletterEventEmitter.off("subscribe", onSubscribe);
    };
  }, [newsletterEventEmitter, trySetVisitorEmail]);

  const email = customer?.email ?? visitorEmail;
  const contextValue = useMemo(
    () => ({ email, setVisitorEmail: trySetVisitorEmail }),
    [email, trySetVisitorEmail],
  );

  return (
    <MmVisitorEmailContext.Provider value={contextValue}>
      {props.children}
    </MmVisitorEmailContext.Provider>
  );
};

const useMmVisitorEmailContext = (): MmVisitorEmailContextValue => {
  const context = useContext(MmVisitorEmailContext);
  if (!context) {
    throw new Error(
      "Visitor email hooks must be used within MmVisitorEmailProvider",
    );
  }
  return context;
};

export const useVisitorEmail = (): string | undefined =>
  useMmVisitorEmailContext().email;

export const useSetVisitorEmail = (): ((
  value: unknown,
) => string | undefined) =>
  useMmVisitorEmailContext().setVisitorEmail;
